import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FibonacciChart } from "./FibonacciChart";
import { TradePanel } from "./TradePanel";
import { AccountSummary } from "./AccountSummary";
import { TradeHistory } from "./TradeHistory";
import { StrategyStatusPanel } from "./StrategyStatusPanel";
import { StrategySettings } from "./StrategySettings";
import { AudioAlerts } from "./AudioAlerts";
import { LimitsDebugPanel } from "./LimitsDebugPanel";
import { FeedStatusIndicator } from "./FeedStatusIndicator";
import { useExecutionWebSocket } from "../hooks/useExecutionWebSocket";
import { TrendingUp, TrendingDown, RefreshCw, Settings, ChevronDown, Activity, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminPanel } from "./AdminPanel";

const API = "/api";

const TIMEFRAMES = ["5m", "15m", "1h", "4h", "1d"];
const SYMBOLS = ["BTC/USD", "ETH/USD", "SOL/USD"];

const adaptFibStructure = (raw, timeframe, tradeStatus = "WAITING") => {
  if (!raw) return null;

  const isBull = (raw.orientation || "").toLowerCase() === "bullish";
  const opacity = tradeStatus === "ARMED" ? 0.45 : (tradeStatus === "LIVE" || tradeStatus === "FILLED" || tradeStatus === "ACTIVE" || tradeStatus === "OPEN") ? 1.0 : 0.25;

  return {
    timeframe: raw.timeframe || timeframe,
    is_uptrend: isBull,
    displayOpacity: opacity,
      anchor_from_price: Number(raw.swing_from_price),
      anchor_from_time: raw.swing_from_time,
      anchor_to_price: Number(raw.swing_to_price),
      anchor_to_time: raw.swing_to_time,
        swing_from_price: Number(raw.swing_from_price),
        swing_from_time: raw.swing_from_time,
        swing_to_price: Number(raw.swing_to_price),
        swing_to_time: raw.swing_to_time,

      swing_high: isBull ? Number(raw.swing_to_price) : Number(raw.swing_from_price),
      swing_low: isBull ? Number(raw.swing_from_price) : Number(raw.swing_to_price),
      swingHighTime: isBull ? raw.swing_to_time : raw.swing_from_time,
      swingLowTime: isBull ? raw.swing_from_time : raw.swing_to_time,
      fib_neg_236: Number(raw.fib_neg_236) || null,
      fib_0:       Number(raw.fib_0)       || null,
      fib_236:     Number(raw.fib_236)     || null,
      fib_382:     Number(raw.fib_382)     || null,
      fib_50:      Number(raw.fib_50)      || null,
      fib_618:     Number(raw.fib_618)     || null,
      fib_100:     Number(raw.fib_100)     || null,
      fib_target:  Number(raw.fib_target)  || null,
      levels: [
        { level: 50.0, price: Number(raw.fib_50), label: "ENTRY", role: "entry" },
        { level: 61.8, price: Number(raw.fib_618), label: "STOP", role: "stop" },
        { level: -23.6, price: Number(raw.fib_target), label: "TARGET 1", role: "target" },
      ],
      };
    };

  export const TradingDashboard = () => {
    const [candles, setCandles] = useState([]);
    const [fibonacci, setFibonacci] = useState(null);
  const [tradeState, setTradeState] = useState(null);
    const [swingPoints, setSwingPoints] = useState([]);

    const [symbol, setSymbol] = useState("BTC/USD");
    const [timeframe, setTimeframe] = useState("15m");
  const [account, setAccount] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tradeMarkers, setTradeMarkers] = useState([]);
  const [showRedrawAnnotation, setShowRedrawAnnotation] = useState(true); // Toggle for FROM/TO annotation
  const [debugMode, setDebugMode] = useState(false); // Debug mode toggle
  const [lastRedrawEvent, setLastRedrawEvent] = useState(null); // Last redraw event for debug
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
    const [marketRegime, setMarketRegime] = useState("range");
    const [historicalLevels, setHistoricalLevels] = useState([]);
  const [countdown, setCountdown] = useState(0);
  useEffect(() => {
    axios.get("/api/auth/me", { withCredentials: true })
      .then(res => setIsAdmin(res.data?.role === "admin"))
      .catch(() => setIsAdmin(false));
  }, []);

    useEffect(() => {
      axios.get(`${API}/historical-levels`)
        .then((res) => setHistoricalLevels(res.data?.levels?.filter((z) => z.display) || []))
        .catch(() => setHistoricalLevels([]));
    }, []);
  useEffect(() => {
    const calc = () => {
      const now = Date.now();
      const secs = timeframe === "5m" ? 300 : timeframe === "15m" ? 900 : timeframe === "1h" ? 3600 : timeframe === "4h" ? 14400 : timeframe === "1d" ? 86400 : 60;
      const next = Math.ceil(now / (secs * 1000)) * secs * 1000;
      setCountdown(Math.max(0, Math.floor((next - now) / 1000)));
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [timeframe]);

    const [currentPrice, setCurrentPrice] = useState(0);
    const [priceChange, setPriceChange] = useState(0);
  const priceRef = useRef(currentPrice);

  // WebSocket handlers for execution-layer responsiveness
  const handlePriceTick = useCallback((data) => {
    const newPrice = data.price;
    const oldPrice = priceRef.current;
    if (oldPrice > 0) {
      setPriceChange(((newPrice - oldPrice) / oldPrice) * 100);
    }
    setCurrentPrice(newPrice);
    priceRef.current = newPrice;
    
    // Update last candle's close price in real-time (execution layer only)
    setCandles(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const lastCandle = { ...updated[updated.length - 1] };
      lastCandle.close = newPrice;
      lastCandle.high = Math.max(lastCandle.high, newPrice);
      lastCandle.low = Math.min(lastCandle.low, newPrice);
      updated[updated.length - 1] = lastCandle;
      return updated;
    });
  }, []);

  const handlePositionUpdate = useCallback((update) => {
    // Update open trades with real-time P&L and trailing stop
    setOpenTrades(prev => prev.map(trade => {
      if (trade.id === update.trade_id) {
        return {
          ...trade,
          current_pnl: update.current_pnl,
          current_price: update.current_price,
          effective_stop: update.effective_stop
        };
      }
      return trade;
    }));
    
    // Notify on trailing stop ratchet
    if (update.trailing_updated) {
      toast.info(`Stop ratcheted to $${update.effective_stop.toFixed(2)}`, {
        duration: 2000,
        icon: "🔒"
      });
    }
  }, []);

  const handleTradeClosed = useCallback((update) => {
    const isProfitable = update.pnl > 0;
    const icon = update.exit_reason === "stop_loss" ? "🛑" : "🎯";
    toast[isProfitable ? "success" : "error"](
      `${update.exit_reason === "stop_loss" ? "Stop Loss" : "Take Profit"} hit at $${update.exit_price.toFixed(2)} (PnL: $${update.pnl >= 0 ? '+' : ''}${update.pnl.toFixed(2)})`,
      { icon, duration: 5000 }
    );
    
    // Refresh trades and account
    fetchOpenTrades();
    fetchAccount();
    fetchTradeHistory();
  }, []);

  // Handle structure redraw events (candle-close based)
  const handleStructureRedraw = useCallback((data) => {
    // Store for debug panel
    setLastRedrawEvent(data);
    
    // Enhanced toast with debug info
    const fromType = data.redraw_from?.type?.replace("_", " ") || "swing";
    const toType = data.redraw_to?.type?.replace("_", " ") || "trigger";
    
    toast.info(
      `Redraw: ${data.prior_direction?.toUpperCase()} → ${data.new_direction?.toUpperCase()} (${data.timeframe}) | FROM ${fromType} TO ${toType}`,
      { duration: 5000, icon: "🔄" }
    );
    
    // Update the fibonacci structure with new data
    if (data.structure) {
      setFibonacci(data.structure);
    }
    // Recalculate market regime based on new bias
    fetchCandles();
  }, []);

  // Connect to execution WebSocket with structure redraw handler
  const { connected: wsConnected, lastPrice: wsPrice, subscribedSymbols } = useExecutionWebSocket(
    handlePriceTick,
    handlePositionUpdate,
    handleTradeClosed,
    handleStructureRedraw,
    [symbol]
  );

  const fetchCandles = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshing(true);

      const candleTfMap = {
        "5m": "5m",
        "15m": "15m",
        "1h": "1h",
        "4h": "4h",
        "1d": "1D",
      };

      const fibTfMap = {
        "5m": "5m",
        "15m": "15m",
        "1h": "1H",
        "4h": "4H",
        "1d": "1D",
      };

      const backendTf = candleTfMap[timeframe] || timeframe;

      const [candleRes, fibRes, stateRes] = await Promise.all([
        axios.get(
          `${API}/candles/${backendTf}?limit=${backendTf === "4H" ? 1200 : 500}`,
          { withCredentials: true }
        ),
        axios.get(`${API}/fib-structures`, { withCredentials: true }),
        axios.get(`${API}/strategy/state`, { withCredentials: true })
      ]);

      const normalizedCandles = (candleRes.data.candles || [])
        .map((c) => {
          const rawTime = c.time ?? c.timestamp;
          const timeMs =
            typeof rawTime === "number"
              ? (rawTime < 1e12 ? rawTime * 1000 : rawTime)
              : Date.parse(rawTime);

          return {
            ...c,
            time: rawTime,
            timeMs,
          };
        })
        .sort((a, b) => (a.timeMs ?? 0) - (b.timeMs ?? 0));

      setCandles(normalizedCandles);

      const tradeStatus =
        stateRes.data?.trade_status ||
        stateRes.data?.trade_state?.status ||
        "WAITING";

      const rawFib = fibRes.data?.[fibTfMap[timeframe]] || null;
      setFibonacci(adaptFibStructure(rawFib, timeframe, tradeStatus));
      setTradeState(stateRes.data?.trade_state || null);

      setSwingPoints([]);

      const bias = stateRes.data?.limits_bias || "no_trade";
      setMarketRegime(
        bias === "long_only" ? "bullish" : bias === "short_only" ? "bearish" : "range"
      );

      if (normalizedCandles.length > 0) {
        const lastCandle = normalizedCandles[normalizedCandles.length - 1];
        setCurrentPrice(lastCandle.close);
        priceRef.current = lastCandle.close;
      }

      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching candles:", error);
      toast.error("Failed to fetch market data");
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeframe]);

  const fetchAccount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/account`, { withCredentials: true });
      setAccount(response.data);
    } catch (error) {
      console.error("Error fetching account:", error);
    }
  }, []);

  const fetchOpenTrades = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sim-trades/open`, { withCredentials: true });
      const trades = response.data.trades || response.data || [];
      setOpenTrades(trades);
      const markers = trades.map(trade => ({
        time: trade.entry_time,
        price: trade.entry_price,
        direction: trade.direction,
        engine: trade.engine || "—",
        label: `${trade.engine || "—"} ${String(trade.direction || "").toUpperCase()} @ ${Number(trade.entry_price).toFixed(0)}`,
        id: trade.id
      }));
      setTradeMarkers(markers);
    } catch (error) {
      console.error("Error fetching open trades:", error);
    }
  }, []);

  const fetchTradeHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sim-trades`, { withCredentials: true });
      setTradeHistory(response.data);
    } catch (error) {
      console.error("Error fetching trade history:", error);
    }
  }, []);

  const fetchTick = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/tick?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}`, { withCredentials: true });
      if (response.data.price) {
        const newPrice = response.data.price;
        const oldPrice = priceRef.current || newPrice;
        setPriceChange(((newPrice - oldPrice) / oldPrice) * 100);
        setCurrentPrice(newPrice);
        priceRef.current = newPrice;

        setCandles(prev => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const last = { ...updated[updated.length - 1] };
          last.close = newPrice;
          last.high = Math.max(last.high, newPrice);
          last.low = Math.min(last.low, newPrice);
          last.time = last.time ?? last.timestamp;
          updated[updated.length - 1] = last;
          return updated;
        });
      }
    } catch (error) {
      console.error("Error fetching tick:", error);
    }
  }, [symbol, timeframe]);

  useEffect(() => {
    fetchCandles();
    fetchAccount();
    fetchOpenTrades();
    fetchTradeHistory();
  }, [fetchCandles, fetchAccount, fetchOpenTrades, fetchTradeHistory]);

  useEffect(() => {
    // LEVELS candle flicker fix:
    // fetchTick owns the live-building candle.
    // fetchCandles only refreshes near timeframe boundaries so it does not
    // overwrite the active candle every 3 seconds.
    const tfSeconds =
      timeframe === "5m" ? 300 :
      timeframe === "15m" ? 900 :
      timeframe === "1h" || timeframe === "1H" ? 3600 :
      timeframe === "4h" || timeframe === "4H" ? 14400 :
      timeframe === "1d" || timeframe === "1D" ? 86400 :
      300;

    let lastRefreshBucket = null;

    const interval = setInterval(() => {
      fetchTick();
      fetchOpenTrades();
      fetchAccount();

      const now = Math.floor(Date.now() / 1000);
      const bucket = Math.floor(now / tfSeconds);
      const secondsIntoBucket = now % tfSeconds;

      // Refresh candles once shortly after a new timeframe candle begins.
      if (secondsIntoBucket <= 10 && lastRefreshBucket !== bucket) {
        lastRefreshBucket = bucket;
        fetchCandles();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [timeframe, fetchTick, fetchOpenTrades, fetchAccount, fetchCandles]);

  useEffect(() => {
    if (loading) fetchCandles();
  }, [timeframe, symbol, loading, fetchCandles]);

  const handleTrade = async (direction, size, stopLoss, takeProfit, note = "") => {
    try {
      const response = await axios.post(`${API}/trades`, {
        entry_price: currentPrice,
        direction,
        size,
        stop_loss: stopLoss,
        take_profit: takeProfit,
        symbol,
        note: note || undefined
      });
      toast.success(`${direction.toUpperCase()} trade opened at $${currentPrice.toFixed(2)}`);
      fetchOpenTrades();
      fetchAccount();
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to open trade";
      toast.error(msg);
      throw error;
    }
  };

  const handleCloseTrade = async () => {
    try {
      const response = await axios.post(`${API}/sim-trades/manual-close`, {}, { withCredentials: true });
      const pnl = response.data?.pnl || 0;

      if (response.data?.closed === false) {
        toast.info(response.data?.message || "No open trade found");
      } else {
        toast.success(<span>Position closed. PnL: <span className={pnl >= 0 ? "text-green-500" : "text-red-500"}>${Number(pnl).toFixed(2)}</span></span>);
      }

      fetchOpenTrades();
      fetchAccount();
      fetchTradeHistory();
      fetchCandles(true);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Failed to close position";
      toast.error(msg);
    }
  };

  const handleResetAccount = async () => {
    const confirmed = window.confirm(
      "Reset account? This will exclude current visible report trades from performance but preserve database records."
    );

    if (!confirmed) return;

    try {
      await axios.post(`${API}/account/reset`, {}, { withCredentials: true });
      toast.success("Account reset to $10,000");
      fetchAccount();
      fetchOpenTrades();
      fetchTradeHistory();
      setTradeMarkers([]);
      window.location.reload();
    } catch (error) {
      toast.error("Failed to reset account");
    }
  };

  const switchFeed = async (sym, tf) => {
    try {
      await axios.post(`${API}/switch-feed?symbol=${encodeURIComponent(sym)}&timeframe=${tf}`);
    } catch (error) {
      console.error("Failed to switch feed:", error);
    }
  };

  const handleSymbolChange = async (newSymbol) => {
    setSymbol(newSymbol);
    await switchFeed(newSymbol, timeframe);
  };

  const handleTimeframeChange = async (newTimeframe) => {
    setTimeframe(newTimeframe);
    await switchFeed(symbol, newTimeframe);
  };

  const simulateBarClose = async () => {
    try {
      const response = await axios.post(`${API}/bar-close?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}`);
      // LIMITS: Check for invalidation instead of fib_redraw
      if (response.data.invalidation_occurred) {
        toast.info(`Structure invalidated: ${response.data.invalidation_reason?.replace(/_/g, ' ')}`);
      }
      fetchCandles();
    } catch (error) {
      toast.error("Failed to simulate bar close");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post("/api/auth/logout", {}, { withCredentials: true });
    } catch (e) {}
    window.location.href = "/";
  };

  const isPriceUp = priceChange >= 0;
  const tfRole = timeframe === "4h" || timeframe === "1h" ? "Parent" : timeframe === "15m" ? "Setup" : "Execution";


    const getMarketAwareness = () => {
      const price = Number(currentPrice);
      if (!Number.isFinite(price) || !historicalLevels || historicalLevels.length === 0) {
        return { support: null, resistance: null, currentZone: null };
      }

      const zones = historicalLevels
        .map((z) => ({
          ...z,
          zone_low: Number(z.zone_low),
          zone_high: Number(z.zone_high),
        }))
        .filter((z) => Number.isFinite(z.zone_low) && Number.isFinite(z.zone_high))
        .sort((a, b) => a.zone_low - b.zone_low);

      const currentZone = zones.find((z) => price >= z.zone_low && price <= z.zone_high);
      const support = [...zones].reverse().find((z) => z.zone_high < price);
      const resistance = zones.find((z) => z.zone_low > price);

      const distPct = (target) => ((target - price) / price) * 100;

      return {
        currentZone,
        support: support ? {
          ...support,
          distancePct: distPct(support.zone_high),
          priceLabel: `$${support.zone_low.toLocaleString("en-US")}–$${support.zone_high.toLocaleString("en-US")}`
        } : null,
        resistance: resistance ? {
          ...resistance,
          distancePct: distPct(resistance.zone_low),
          priceLabel: `$${resistance.zone_low.toLocaleString("en-US")}–$${resistance.zone_high.toLocaleString("en-US")}`
        } : null,
      };
    };

    const marketAwareness = getMarketAwareness();

  return (
    <div className="h-screen flex flex-col bg-black" data-testid="trading-dashboard">
      <header className="h-12 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="font-mono text-sm font-semibold tracking-wide hover:bg-zinc-800 gap-2" data-testid="symbol-selector">
                <Activity className="w-4 h-4 text-fuchsia-500" />
                {symbol}
                <ChevronDown className="w-3 h-3 text-zinc-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
              {SYMBOLS.map((s) => (
                <DropdownMenuItem key={s} onClick={() => handleSymbolChange(s)} className={`font-mono text-sm ${symbol === s ? 'text-fuchsia-500' : ''}`}>{s}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-3">
            <span className={`font-mono text-lg font-semibold tracking-wide ${isPriceUp ? 'text-green-500' : 'text-red-500'}`} data-testid="current-price">${currentPrice.toFixed(2)}</span>
            <span className={`flex items-center gap-1 text-xs font-mono ${isPriceUp ? 'text-green-500' : 'text-red-500'}`}>
              {isPriceUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {Math.abs(priceChange).toFixed(2)}%
            </span>
          </div>

          <div className={`px-2 py-1 rounded text-xs font-mono uppercase ${marketRegime === "bullish" ? "bg-green-900/30 text-green-500" : marketRegime === "bearish" ? "bg-red-900/30 text-red-500" : "bg-yellow-900/30 text-yellow-500"}`} data-testid="market-bias-badge">
            {openTrades.length > 0 ? (openTrades[0].direction === "LONG" ? "LONG TRADE" : "SHORT TRADE") : (marketRegime === "bullish" ? "LONG ONLY" : marketRegime === "bearish" ? "SHORT ONLY" : "NO TRADE")}
          </div>
          <div className="text-xs font-mono text-purple-400 border border-purple-900/50 bg-purple-950/30 px-2 py-1 rounded">
            <span className="text-purple-500">{timeframe.toUpperCase()} closes in </span>
            <span className="text-purple-200">{Math.floor(countdown / 60).toString().padStart(2, "0")}:{(countdown % 60).toString().padStart(2, "0")}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex gap-1">
{TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                onClick={() => handleTimeframeChange(tf)}
                className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`}
                data-testid={`timeframe-${tf}`}
              >
                {tf}
                {timeframe === tf && <span className="ml-1 text-xs opacity-60">({tf === "4h" || tf === "1h" ? "P" : tf === "15m" ? "S" : "E"})</span>}
              </button>
            ))}
          </div>

          <Button variant="ghost" size="icon" onClick={simulateBarClose} className="hover:bg-zinc-800" title="Simulate Bar Close" data-testid="bar-close-btn">
            <BarChart2 className="w-4 h-4" />
          </Button>

          <Button variant="ghost" size="icon" onClick={() => fetchCandles(true)} disabled={refreshing} className="hover:bg-zinc-800" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>

            <Button
              variant="ghost"
              size="sm"
              data-testid="manual-close-position-btn"
              className="hover:bg-red-900/40 text-red-400 hover:text-red-300 border border-red-900/50 font-mono text-xs uppercase tracking-wider"
              onClick={() => {
                if (!openTrades.length) {
                  toast.error("No open trade");
                  return;
                }
                if (!window.confirm("Close active paper trade at current market price?")) return;
                handleCloseTrade();
              }}
            >
              Close Position
            </Button>


          {/* Real-Time Feed Status Indicator */}
          <FeedStatusIndicator compact={true} />

          {/* Audio Alerts */}
          <AudioAlerts />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-zinc-800"><Settings className="w-4 h-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 w-56">
              <DropdownMenuItem onClick={handleResetAccount} className="text-red-500 focus:text-red-500" data-testid="reset-account-btn">Reset Account</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setShowAdmin(true)}
              className="hover:bg-zinc-800 text-purple-400 hover:text-purple-300 font-mono text-xs uppercase tracking-wider">
              Admin
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout}
            className="hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 font-mono text-xs uppercase tracking-wider">
            Logout
          </Button>

          {account && (
            <div className="pl-4 border-l border-zinc-800">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Account Equity</div>
              <div
                className={`font-mono text-sm font-semibold ${
                  (account.equity ?? 10000) > 10000
                    ? "text-green-400"
                    : (account.equity ?? 10000) < 10000
                    ? "text-red-400"
                    : "text-zinc-200"
                }`}
                data-testid="account-equity"
              >
                ${(account.equity ?? 10000).toFixed(2)}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative">
          <FibonacciChart 
            candles={candles} 
            fibonacci={fibonacci} 
            tradeMarkers={tradeMarkers} 
            loading={loading} 
            swingPoints={swingPoints} 
            showRedrawAnnotation={debugMode && showRedrawAnnotation}
          />
          {/* Toggle for redraw annotation - only visible in debug mode */}
          {debugMode && (
            <button
              onClick={() => setShowRedrawAnnotation(!showRedrawAnnotation)}
              className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-mono z-20 transition-colors ${showRedrawAnnotation ? 'bg-purple-900/50 text-purple-400 border border-purple-700' : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700'}`}
              title={showRedrawAnnotation ? "Hide FROM/TO annotation" : "Show FROM/TO annotation"}
              data-testid="toggle-annotation-btn"
            >
              {showRedrawAnnotation ? "📍 ON" : "📍 OFF"}
            </button>
          )}
        </div>

        <div className="w-[420px] border-l border-zinc-800 bg-zinc-950 flex flex-col shrink-0">
          <Tabs defaultValue="trade" className="flex-1 flex flex-col">
            <TabsList className="w-full justify-start rounded-none border-b border-zinc-800 bg-transparent h-10 px-2">
              <TabsTrigger value="trade" className="font-mono text-xs uppercase tracking-wider data-[state=active]:bg-zinc-800 rounded-sm" data-testid="trade-tab">Trade</TabsTrigger>
              <TabsTrigger value="positions" className="font-mono text-xs uppercase tracking-wider data-[state=active]:bg-zinc-800 rounded-sm" data-testid="positions-tab">Pos ({openTrades.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="trade" className="flex-1 m-0 p-5 overflow-auto space-y-5">
              <StrategyStatusPanel currentPrice={currentPrice} openTrades={openTrades} />
                <div className="p-3 bg-zinc-950 border border-yellow-900/40 rounded space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-yellow-400 font-semibold uppercase tracking-[0.2em]">Market Awareness</div>
                    <div className="text-[10px] text-yellow-500/70 uppercase">V2 Visual</div>
                  </div>

                  {marketAwareness.currentZone && (
                    <div className="p-2 bg-yellow-950/20 border border-yellow-900/40 rounded">
                      <div className="text-[10px] text-yellow-500 uppercase tracking-wider">Current Zone</div>
                      <div className="text-xs text-yellow-300 font-mono">{marketAwareness.currentZone.label}</div>
                      <div className="text-[11px] text-zinc-400 font-mono">
                        ${Number(marketAwareness.currentZone.zone_low).toLocaleString("en-US")}–${Number(marketAwareness.currentZone.zone_high).toLocaleString("en-US")}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-zinc-900/70 border border-zinc-800 rounded">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Nearest Support</div>
                      {marketAwareness.support ? (
                        <>
                          <div className="text-xs text-yellow-300 font-mono truncate">{marketAwareness.support.label}</div>
                          <div className="text-[11px] text-zinc-400 font-mono">{marketAwareness.support.priceLabel}</div>
                          <div className="text-[11px] text-green-400 font-mono mt-1">
                            {Math.abs(marketAwareness.support.distancePct).toFixed(1)}% below
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-zinc-500">—</div>
                      )}
                    </div>

                    <div className="p-2 bg-zinc-900/70 border border-zinc-800 rounded">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Nearest Resistance</div>
                      {marketAwareness.resistance ? (
                        <>
                          <div className="text-xs text-yellow-300 font-mono truncate">{marketAwareness.resistance.label}</div>
                          <div className="text-[11px] text-zinc-400 font-mono">{marketAwareness.resistance.priceLabel}</div>
                          <div className="text-[11px] text-red-400 font-mono mt-1">
                            {Math.abs(marketAwareness.resistance.distancePct).toFixed(1)}% above
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-zinc-500">—</div>
                      )}
                    </div>
                  </div>
                </div>
              <TradePanel currentPrice={currentPrice} account={account} fibonacci={fibonacci} tradeState={tradeState} />
            </TabsContent>

            <TabsContent value="positions" className="flex-1 m-0 overflow-auto">
              <div className="p-4">
                <AccountSummary account={account} openTrades={openTrades} currentPrice={currentPrice} onCloseTrade={handleCloseTrade} />
              </div>
              <div className="border-t border-zinc-800">
                <div className="px-4 py-2 text-xs text-zinc-500 uppercase tracking-wider">Trade History</div>
                <TradeHistory trades={tradeHistory} />
              </div>
            </TabsContent>

            <TabsContent value="settings" className="flex-1 m-0 p-4 overflow-auto">
              <StrategySettings onSettingsChange={() => fetchCandles(true)} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* LIMITS Debug Panel */}
      <LimitsDebugPanel 
        onRedrawEvent={lastRedrawEvent}
        debugMode={debugMode}
        onToggleDebug={() => setDebugMode(!debugMode)}
      />
      {showAdmin && (
        <AdminPanel onClose={() => setShowAdmin(false)} />
      )}
    </div>
  );
};
